import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Phone, Users, Trophy, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { dashboardApi } from '../../lib/api.js';
import { Skeleton } from '../ui/Skeleton.js';
import { BestLocationsList } from './BestLocationsList.js';
import { AgentLeaderboardTable } from './AgentLeaderboardTable.js';
import { RecentTouchesFeed } from './RecentTouchesFeed.js';
import type { KpiPeriod } from '@imza/shared';

// -----------------------------------------------------------------------
// Period selector
// -----------------------------------------------------------------------

const PERIOD_OPTIONS: Array<{ value: KpiPeriod; label: string }> = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: '7 days' },
  { value: 'month', label: '30 days' },
];

// -----------------------------------------------------------------------
// Delta badge — shows % change vs prior period
// -----------------------------------------------------------------------

function calcDelta(cur: number, prv: number): { pct: number | null; up: boolean } {
  if (prv === 0) return { pct: cur > 0 ? null : null, up: cur >= 0 };
  const pct = Math.round(((cur - prv) / prv) * 100);
  return { pct, up: pct >= 0 };
}

function DeltaBadge({ cur, prv }: { cur: number; prv: number }) {
  const { pct, up } = calcDelta(cur, prv);

  if (pct === null) {
    return <span className="text-[10px] text-secondary">—</span>;
  }

  if (pct === 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] text-secondary">
        <Minus className="w-2.5 h-2.5" aria-hidden="true" /> 0%
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[10px] font-semibold ${up ? 'text-green-600' : 'text-red-500'}`}
      title={`Prior period: ${prv}`}
    >
      {up ? (
        <TrendingUp className="w-3 h-3" aria-hidden="true" />
      ) : (
        <TrendingDown className="w-3 h-3" aria-hidden="true" />
      )}
      {Math.abs(pct)}%
    </span>
  );
}

// -----------------------------------------------------------------------
// Achievement tile
// -----------------------------------------------------------------------

interface AchievementTileProps {
  icon: React.ElementType;
  iconColor: string;
  iconBg: string;
  label: string;
  cur: number;
  prv: number;
  isLoading: boolean;
  delay?: number;
}

function AchievementTile({
  icon: Icon,
  iconColor,
  iconBg,
  label,
  cur,
  prv,
  isLoading,
  delay = 0,
}: AchievementTileProps) {
  return (
    <div
      className="bg-white border border-border rounded-xl p-4 flex flex-col gap-2 animate-fade-in"
      style={{ animationDelay: `${delay}ms`, animationFillMode: 'both' }}
    >
      <div className="flex items-center justify-between">
        <div className={`w-8 h-8 rounded-lg ${iconBg} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-4 h-4 ${iconColor}`} aria-hidden="true" />
        </div>
        {!isLoading && <DeltaBadge cur={cur} prv={prv} />}
      </div>
      {isLoading ? (
        <Skeleton className="h-8 w-16" />
      ) : (
        <p className="text-3xl font-bold text-primary-text tabular-nums animate-count-up">{cur.toLocaleString()}</p>
      )}
      <p className="text-xs text-secondary font-medium">{label}</p>
    </div>
  );
}

// -----------------------------------------------------------------------
// Main AdminDashboard component
// -----------------------------------------------------------------------

export function AdminDashboard() {
  const [period, setPeriod] = useState<KpiPeriod>('week');

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'admin', period],
    queryFn: () => dashboardApi.admin(period),
    staleTime: 2 * 60_000,
  });

  const ach = data?.achievements;

  return (
    <div className="space-y-5">
      {/* Period selector */}
      <div className="flex gap-2" role="group" aria-label="Period selector">
        {PERIOD_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => setPeriod(opt.value)}
            aria-pressed={period === opt.value}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium min-h-[44px] transition-colors ${
              period === opt.value
                ? 'bg-primary text-white'
                : 'bg-hover-bg text-secondary hover:text-primary-text'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Achievements strip — 2 cols mobile, 4 desktop */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-secondary mb-3">Achievements</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <AchievementTile
            icon={Phone}
            iconColor="text-blue-600"
            iconBg="bg-blue-50"
            label="Outreach (calls + WA)"
            cur={ach?.outreachCur ?? 0}
            prv={ach?.outreachPrv ?? 0}
            isLoading={isLoading}
            delay={0}
          />
          <AchievementTile
            icon={TrendingUp}
            iconColor="text-purple-600"
            iconBg="bg-purple-50"
            label="Pipeline moves"
            cur={ach?.movesCur ?? 0}
            prv={ach?.movesPrv ?? 0}
            isLoading={isLoading}
            delay={80}
          />
          <AchievementTile
            icon={Users}
            iconColor="text-amber-600"
            iconBg="bg-amber-50"
            label="Landlords reached"
            cur={ach?.reachedCur ?? 0}
            prv={ach?.reachedPrv ?? 0}
            isLoading={isLoading}
            delay={160}
          />
          <AchievementTile
            icon={Trophy}
            iconColor="text-green-600"
            iconBg="bg-green-50"
            label="Wins (sold / rented)"
            cur={ach?.winsCur ?? 0}
            prv={ach?.winsPrv ?? 0}
            isLoading={isLoading}
            delay={240}
          />
        </div>
      </div>

      {/* Best locations + Recent touches — side-by-side on wide screens */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <BestLocationsList
          locations={data?.bestLocations ?? []}
          isLoading={isLoading}
        />
        <RecentTouchesFeed
          items={data?.recentlyTouched ?? []}
          isLoading={isLoading}
        />
      </div>

      {/* Agent leaderboard — full width */}
      <AgentLeaderboardTable
        agents={data?.topAgents ?? []}
        isLoading={isLoading}
      />
    </div>
  );
}
